import { Mark, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { EditorState } from '@tiptap/pm/state'

import {
  FormattingChanges,
  LIST_TYPE_BULLET,
  ListChange,
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_DELETION,
  SUGGESTION_TYPE_FORMATTING,
  SUGGESTION_TYPE_INSERTION,
  SuggestionType,
} from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { stringArraysEqual } from '../../../utils'
import { listChangeArraysEqual } from './list-track-changes'

export type SuggestionTextRun = {
  text: string
  marks: string[]
}

export type SuggestionCategory = 'added' | 'removed' | 'replaced' | 'formatting'

export type SuggestionSummary = {
  id: string
  category: SuggestionCategory
  authorName: string | null
  authorId: number | null
  createdAt: string | null
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingAdded: string[]
  formattingRemoved: string[]
  listChanges: ListChange[]
}

type SuggestionRange = {
  from: number
  to: number
  type: SuggestionType
  formattingChanges: FormattingChanges | null
}

type SuggestionAtPosition = {
  suggestionId: string
  type: SuggestionType
  authorName: string | null
  createdAt: string | null
}

type SuggestionContent = {
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingAdded: string[]
  formattingRemoved: string[]
  listChanges: ListChange[]
}

// Maps schema mark names to the suffix used in EDITOR.TRACK_CHANGES.FORMAT_<KEY> /
// FORMAT_NOT_<KEY> i18n keys. Adding a new tracked formatting mark requires adding
// an entry here AND the corresponding translation keys in every locale.
export const FORMATTING_MARK_DISPLAY_KEYS: Record<string, string> = {
  strong: 'BOLD',
  italic: 'ITALIC',
}

export type FormattingLabelDescriptor = {
  key: string
  negated: boolean
}

export function collectFormattingLabelDescriptors(added: string[], removed: string[]): FormattingLabelDescriptor[] {
  const descriptors: FormattingLabelDescriptor[] = []
  for (const markName of added) {
    const key = FORMATTING_MARK_DISPLAY_KEYS[markName] ?? null
    if (key !== null) {
      descriptors.push({ key, negated: false })
    }
  }
  for (const markName of removed) {
    const key = FORMATTING_MARK_DISPLAY_KEYS[markName] ?? null
    if (key !== null) {
      descriptors.push({ key, negated: true })
    }
  }
  return descriptors
}

export type ListChangeLabelDescriptor = {
  key: string
  params?: Record<string, number>
}

export function collectListChangeLabelDescriptors(changes: ListChange[]): ListChangeLabelDescriptor[] {
  const descriptors: ListChangeLabelDescriptor[] = []
  for (const change of changes) {
    if (change.kind === 'wrap') {
      descriptors.push({ key: change.listType === LIST_TYPE_BULLET ? 'LIST_WRAPPED_BULLET' : 'LIST_WRAPPED_NUMBERED' })
      continue
    }
    if (change.kind === 'unwrap') {
      descriptors.push({ key: change.listType === LIST_TYPE_BULLET ? 'LIST_UNWRAPPED_BULLET' : 'LIST_UNWRAPPED_NUMBERED' })
      continue
    }
    if (change.kind === 'changeListType') {
      descriptors.push({ key: change.to === LIST_TYPE_BULLET ? 'LIST_TYPE_CHANGED_TO_BULLET' : 'LIST_TYPE_CHANGED_TO_NUMBERED' })
      continue
    }
    if (change.delta === 0) {
      continue
    }
    const count = Math.abs(change.delta)
    const base = change.delta > 0 ? 'LIST_INDENTED' : 'LIST_OUTDENTED'
    descriptors.push({ key: count === 1 ? base : `${base}_PLURAL`, params: { count } })
  }
  return descriptors
}

const NON_DISPLAY_MARK_NAMES = new Set([SUGGESTION_MARK_NAME, 'chunk'])

export function collectDisplayMarkNames(marks: readonly { type: { name: string } }[]): string[] {
  return marks.filter((mark) => !NON_DISPLAY_MARK_NAMES.has(mark.type.name)).map((mark) => mark.type.name)
}

type SuggestionVisit = {
  node: ProseMirrorNode
  pos: number
  mark: Mark
  type: SuggestionType
  formattingChanges: FormattingChanges | null
}

function forEachSuggestionTextNode(state: EditorState, suggestionId: string, visit: (entry: SuggestionVisit) => void): void {
  state.doc.descendants((node, pos) => {
    if (!node.isText) {
      return true
    }

    const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['suggestionId'] === suggestionId) ?? null
    if (suggestionMark === null) {
      return false
    }

    const type = suggestionMark.attrs['type'] as SuggestionType | null
    if (type !== SUGGESTION_TYPE_INSERTION && type !== SUGGESTION_TYPE_DELETION && type !== SUGGESTION_TYPE_FORMATTING) {
      return false
    }

    const formattingChanges = (suggestionMark.attrs['formattingChanges'] as FormattingChanges | null) ?? null
    visit({ node, pos, mark: suggestionMark, type, formattingChanges })
    return false
  })
}

export function collectSuggestionStartPositions(state: EditorState): number[] {
  const seen = new Set<string>()
  const positions: number[] = []

  state.doc.descendants((node, pos) => {
    if (!node.isText) {
      return true
    }
    const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
    if (suggestionMark === null) {
      return false
    }
    const suggestionId = suggestionMark.attrs['suggestionId'] as string | null
    if (suggestionId === null || seen.has(suggestionId)) {
      return false
    }
    seen.add(suggestionId)
    positions.push(pos)
    return false
  })

  return positions
}

export function findAllRangesForSuggestion(state: EditorState, suggestionId: string): SuggestionRange[] {
  const ranges: SuggestionRange[] = []
  forEachSuggestionTextNode(state, suggestionId, ({ node, pos, type, formattingChanges }) => {
    ranges.push({ from: pos, to: pos + node.nodeSize, type, formattingChanges })
  })
  return ranges
}

export function findSuggestionAtPosition(state: EditorState, position: number): SuggestionAtPosition | null {
  const resolved = state.doc.resolve(position)

  const fromMarks = resolved.marks().find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
  const fromBefore = resolved.nodeBefore?.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
  const fromAfter = resolved.nodeAfter?.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
  const suggestionMark = fromMarks ?? fromBefore ?? fromAfter ?? null

  if (suggestionMark === null) {
    return null
  }

  const suggestionId = suggestionMark.attrs['suggestionId'] as string | null
  const type = suggestionMark.attrs['type'] as SuggestionType | null
  const authorName = suggestionMark.attrs['authorName'] as string | null
  const createdAt = (suggestionMark.attrs['createdAt'] as string | null) ?? null

  if (suggestionId === null || (type !== SUGGESTION_TYPE_INSERTION && type !== SUGGESTION_TYPE_DELETION && type !== SUGGESTION_TYPE_FORMATTING)) {
    return null
  }

  return { suggestionId, type, authorName, createdAt }
}

export function collectSuggestionContent(state: EditorState, suggestionId: string): SuggestionContent {
  const insertionRuns: SuggestionTextRun[] = []
  const deletionRuns: SuggestionTextRun[] = []
  const addedSet = new Set<string>()
  const removedSet = new Set<string>()
  const listChanges: ListChange[] = []
  const seenFormattingMarks = new Set<Mark>()

  forEachSuggestionTextNode(state, suggestionId, ({ node, mark, type, formattingChanges }) => {
    const text = node.text ?? ''
    if (type === SUGGESTION_TYPE_INSERTION) {
      insertionRuns.push({ text, marks: collectDisplayMarkNames(node.marks) })
    } else if (type === SUGGESTION_TYPE_DELETION) {
      deletionRuns.push({ text, marks: collectDisplayMarkNames(node.marks) })
    } else if (type === SUGGESTION_TYPE_FORMATTING && formattingChanges !== null) {
      for (const markName of formattingChanges.added) {
        addedSet.add(markName)
      }
      for (const markName of formattingChanges.removed) {
        removedSet.add(markName)
      }
      if (formattingChanges.listChanges !== undefined && !seenFormattingMarks.has(mark)) {
        seenFormattingMarks.add(mark)
        for (const listChange of formattingChanges.listChanges) {
          listChanges.push(listChange)
        }
      }
    }
  })

  return {
    insertionRuns,
    deletionRuns,
    formattingAdded: Array.from(addedSet),
    formattingRemoved: Array.from(removedSet),
    listChanges,
  }
}

type SummaryBuilder = {
  id: string
  authorName: string | null
  authorId: number | null
  createdAt: string | null
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingAddedSet: Set<string>
  formattingRemovedSet: Set<string>
  listChanges: ListChange[]
  seenFormattingMarks: Set<Mark>
}

export function suggestionRunsEqual(left: SuggestionTextRun[], right: SuggestionTextRun[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  for (let index = 0; index < left.length; index++) {
    if (left[index].text !== right[index].text) {
      return false
    }
    if (!stringArraysEqual(left[index].marks, right[index].marks)) {
      return false
    }
  }
  return true
}

export function suggestionSummariesEqual(left: SuggestionSummary[], right: SuggestionSummary[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  for (let index = 0; index < left.length; index++) {
    const leftSummary = left[index]
    const rightSummary = right[index]
    if (
      leftSummary.id !== rightSummary.id ||
      leftSummary.category !== rightSummary.category ||
      leftSummary.authorName !== rightSummary.authorName ||
      leftSummary.authorId !== rightSummary.authorId ||
      leftSummary.createdAt !== rightSummary.createdAt
    ) {
      return false
    }
    if (
      !suggestionRunsEqual(leftSummary.insertionRuns, rightSummary.insertionRuns) ||
      !suggestionRunsEqual(leftSummary.deletionRuns, rightSummary.deletionRuns)
    ) {
      return false
    }
    if (
      !stringArraysEqual(leftSummary.formattingAdded, rightSummary.formattingAdded) ||
      !stringArraysEqual(leftSummary.formattingRemoved, rightSummary.formattingRemoved)
    ) {
      return false
    }
    if (!listChangeArraysEqual(leftSummary.listChanges, rightSummary.listChanges)) {
      return false
    }
  }
  return true
}

export function categorizeSuggestion(insertionRuns: SuggestionTextRun[], deletionRuns: SuggestionTextRun[]): SuggestionCategory {
  if (insertionRuns.length > 0 && deletionRuns.length > 0) {
    return 'replaced'
  }
  if (deletionRuns.length > 0) {
    return 'removed'
  }
  if (insertionRuns.length > 0) {
    return 'added'
  }
  return 'formatting'
}

export function collectAllSuggestions(state: EditorState): SuggestionSummary[] {
  const builders = new Map<string, SummaryBuilder>()
  const order: string[] = []

  state.doc.descendants((node) => {
    if (!node.isText) {
      return true
    }

    const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
    if (suggestionMark === null) {
      return false
    }

    const id = suggestionMark.attrs['suggestionId'] as string | null
    const type = suggestionMark.attrs['type'] as SuggestionType | null
    if (id === null || (type !== SUGGESTION_TYPE_INSERTION && type !== SUGGESTION_TYPE_DELETION && type !== SUGGESTION_TYPE_FORMATTING)) {
      return false
    }

    let builder = builders.get(id) ?? null
    if (builder === null) {
      builder = {
        id,
        authorName: (suggestionMark.attrs['authorName'] as string | null) ?? null,
        authorId: (suggestionMark.attrs['authorId'] as number | null) ?? null,
        createdAt: (suggestionMark.attrs['createdAt'] as string | null) ?? null,
        insertionRuns: [],
        deletionRuns: [],
        formattingAddedSet: new Set<string>(),
        formattingRemovedSet: new Set<string>(),
        listChanges: [],
        seenFormattingMarks: new Set<Mark>(),
      }
      builders.set(id, builder)
      order.push(id)
    }

    const text = node.text ?? ''
    if (type === SUGGESTION_TYPE_INSERTION) {
      builder.insertionRuns.push({ text, marks: collectDisplayMarkNames(node.marks) })
    } else if (type === SUGGESTION_TYPE_DELETION) {
      builder.deletionRuns.push({ text, marks: collectDisplayMarkNames(node.marks) })
    } else if (type === SUGGESTION_TYPE_FORMATTING) {
      const formattingChanges = (suggestionMark.attrs['formattingChanges'] as FormattingChanges | null) ?? null
      if (formattingChanges !== null) {
        for (const markName of formattingChanges.added) {
          builder.formattingAddedSet.add(markName)
        }
        for (const markName of formattingChanges.removed) {
          builder.formattingRemovedSet.add(markName)
        }
        if (formattingChanges.listChanges !== undefined && !builder.seenFormattingMarks.has(suggestionMark)) {
          builder.seenFormattingMarks.add(suggestionMark)
          for (const listChange of formattingChanges.listChanges) {
            builder.listChanges.push(listChange)
          }
        }
      }
    }

    return false
  })

  return order.map((id) => {
    const builder = builders.get(id)!
    return {
      id: builder.id,
      category: categorizeSuggestion(builder.insertionRuns, builder.deletionRuns),
      authorName: builder.authorName,
      authorId: builder.authorId,
      createdAt: builder.createdAt,
      insertionRuns: builder.insertionRuns,
      deletionRuns: builder.deletionRuns,
      formattingAdded: Array.from(builder.formattingAddedSet),
      formattingRemoved: Array.from(builder.formattingRemovedSet),
      listChanges: builder.listChanges,
    }
  })
}
